import { useState, useEffect } from 'react';
import {
  apenasDigitos,
  limparDocumento,
  mascaraCnpj,
  mascaraCpf,
  mascaraTelefone,
  validarCnpj,
  validarCpf,
  validarEmail,
  validarTelefone,
} from '@/lib/mascaras';
import { sugerirCorrecaoDeEmail } from '@/lib/emailTypo';
import { verificarDominioDeEmail } from '@/lib/emailDominio';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, edgeFunctionErrorBody } from '@/lib/supabase';
import { planosDisponiveis, fetchPlanosFromDB, PlanInfo } from '@/lib/planConfig';
import {
  gravarCadastroPendente,
  limparCadastroPendente,
  restaurarCadastroPendente,
} from '@/lib/cadastroPendente';
import Logo from '@/components/base/Logo';
import PlanoEscolha from '@/components/feature/PlanoEscolha';

const featureItems = [
  { icon: 'ri-building-line', label: 'Condomínios', desc: 'Gestão completa' },
  { icon: 'ri-shield-check-line', label: 'Alvarás', desc: 'Licenças e vistorias' },
  { icon: 'ri-tools-line', label: 'Manutenções', desc: 'Preventiva e corretiva' },
  { icon: 'ri-server-line', label: 'Infraestrutura', desc: 'Equipamentos e alertas' },
  { icon: 'ri-heart-pulse-line', label: 'Saúde', desc: 'Pendências e riscos' },
  { icon: 'ri-user-settings-line', label: 'Funcionários', desc: 'Cadastro e atividades' },
];

type AccountType = 'pf' | 'pj';
type Step = 1 | 2;

interface RegisterForm {
  tipo: AccountType;
  nome: string;
  cpf: string;
  razaoSocial: string;
  cnpj: string;
  nomeResponsavel: string;
  email: string;
  telefone: string;
  senha: string;
  confirmarSenha: string;
}

const emptyForm: RegisterForm = {
  tipo: 'pf',
  nome: '',
  cpf: '',
  razaoSocial: '',
  cnpj: '',
  nomeResponsavel: '',
  email: '',
  telefone: '',
  senha: '',
  confirmarSenha: '',
};

function validateStep1(form: RegisterForm): string | null {
  if (form.tipo === 'pf') {
    if (!form.nome.trim()) return 'O nome completo é obrigatório.';
    if (!validarCpf(form.cpf)) return 'Informe um CPF válido.';
  } else {
    if (!form.razaoSocial.trim()) return 'A razão social é obrigatória.';
    if (!validarCnpj(form.cnpj)) return 'Informe um CNPJ válido.';
    if (!form.nomeResponsavel.trim()) return 'O nome do responsável é obrigatório.';
  }
  // `includes('@')` aceitava "a@b": e-mail precisa de domínio e TLD.
  if (!validarEmail(form.email)) return 'Informe um e-mail válido.';
  if (!validarTelefone(form.telefone)) return 'Informe um telefone válido.';
  if (!form.senha || form.senha.length < 6) return 'A senha deve ter pelo menos 6 caracteres.';
  if (form.senha !== form.confirmarSenha) return 'As senhas não conferem.';
  return null;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<RegisterForm>(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [emailConfirmRequired, setEmailConfirmRequired] = useState(false);

  // Sugestão de correção do domínio (P-23). Confirmar por e-mail prova posse,
  // não previne erro de digitação — e aqui é o único momento em que a pessoa
  // ainda está na frente do campo para corrigir.
  const [sugestaoEmail, setSugestaoEmail] = useState<string | null>(null);

  // Em estado, e nao lendo `planosDisponiveis` no render: a lista do modulo e
  // atualizada por fora do React, entao a tela ficava nos tres planos do
  // FALLBACK ate um clique forcar novo render.
  const [planos, setPlanos] = useState<PlanInfo[]>(planosDisponiveis);

  useEffect(() => {
    let ativo = true;

    fetchPlanosFromDB().then((lista) => {
      if (ativo) setPlanos(lista);
    });

    return () => {
      ativo = false;
    };
  }, []);

  // Retoma o cadastro interrompido — e so o de quem esta na sessao atual.
  // Depois de um logout, o estado e descartado e o cadastro recomeca no
  // formulario. Ver `lib/cadastroPendente.ts`.
  useEffect(() => {
    let ativo = true;

    restaurarCadastroPendente().then((pendente) => {
      if (!ativo || !pendente) return;
      setForm((prev) => ({ ...prev, email: pendente.email }));
      setStep(2);
    });

    return () => {
      ativo = false;
    };
  }, []);

  const updateField = (field: keyof RegisterForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleTipoChange = (tipo: AccountType) => {
    setForm((prev) => ({ ...prev, tipo }));
    setError('');
  };

  const clearPendingState = () => {
    limparCadastroPendente();
    setEmailConfirmRequired(false);
  };

  const handleNextStep = async () => {
    const validationError = validateStep1(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setLoading(true);
    setEmailConfirmRequired(false);

    // Domínio inexistente é erro de digitação, não escolha: nenhum e-mail
    // chegaria ali, e a pessoa descobriria isso depois de já ter pago. Recusar
    // aqui não custa usuário real.
    //
    // Só `inexistente` barra. `sem-mx` é ambíguo e `indeterminado` significa
    // que a checagem falhou — recusar por problema nosso seria pior que aceitar
    // um endereço ruim, que a verificação por e-mail ainda pega depois.
    const estadoDoDominio = await verificarDominioDeEmail(form.email);
    if (estadoDoDominio === 'inexistente') {
      const alternativa = sugerirCorrecaoDeEmail(form.email);
      setSugestaoEmail(alternativa);
      setError(
        alternativa
          ? `O domínio de "${form.email.trim()}" não existe. Você quis dizer ${alternativa}?`
          : `O domínio de "${form.email.trim()}" não existe. Confira o endereço.`,
      );
      setLoading(false);
      return;
    }

    // 1. Create auth user via Supabase
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: form.email.trim().toLowerCase(),
      password: form.senha,
      options: {
        data: {
          tipo: form.tipo,
          nome: form.tipo === 'pf' ? form.nome.trim() : form.razaoSocial.trim(),
        },
      },
    });

    if (signUpError) {
      if (
        signUpError.message.includes('already registered') ||
        signUpError.message.includes('already exists')
      ) {
        setError('Este e-mail já está cadastrado. Tente fazer login.');
      } else if (
        signUpError.message.includes('rate limit') ||
        signUpError.message.includes('rate_limit')
      ) {
        setError(
          'Você fez muitas tentativas em pouco tempo. Aguarde alguns minutos ou use outro e-mail para testar.',
        );
      } else {
        setError(signUpError.message);
      }
      setLoading(false);
      return;
    }

    if (!signUpData.user) {
      setError('Erro ao criar conta. Tente novamente.');
      setLoading(false);
      return;
    }

    const userId = signUpData.user.id;
    gravarCadastroPendente(userId, form.email.trim().toLowerCase());

    // 2. If we have an active session (email confirmation disabled), create account + profile immediately
    if (signUpData.session) {
      const { data: accountId, error: accountError } = await supabase.rpc('criar_account');

      if (accountError || !accountId) {
        // `unique_violation` aqui é a recusa deliberada de refazer um cadastro
        // já concluído (migration 0018), e não uma falha técnica: a pessoa
        // precisa entrar, não cadastrar de novo.
        if (accountError?.code === '23505') {
          setError('Esta conta já foi cadastrada. Entre com o seu e-mail e senha.');
          clearPendingState();
          setLoading(false);
          return;
        }

        console.error('Error creating account:', accountError?.message);
        setLoading(false);
        setStep(2);
        return;
      }

      const profileData =
        form.tipo === 'pf'
          ? {
              id: userId,
              tipo: 'pf' as const,
              nome: form.nome.trim(),
              cpf: limparDocumento(form.cpf, 'pf'),
              razao_social: null,
              cnpj: null,
              nome_responsavel: null,
              email: form.email.trim().toLowerCase(),
              telefone: apenasDigitos(form.telefone),
              account_id: accountId,
            }
          : {
              id: userId,
              tipo: 'pj' as const,
              nome: form.nomeResponsavel.trim(),
              cpf: null,
              razao_social: form.razaoSocial.trim(),
              cnpj: limparDocumento(form.cnpj, 'pj'),
              nome_responsavel: form.nomeResponsavel.trim(),
              email: form.email.trim().toLowerCase(),
              telefone: apenasDigitos(form.telefone),
              account_id: accountId,
            };

      // `update`, e nao `upsert`: `criar_account` ja criou a linha do perfil com
      // o vinculo da conta, e este passo completa o esboco. O filtro por
      // `cadastro_concluido_em is null` faz a segunda tentativa não alterar
      // nada, ainda que alguém chegue aqui por outro caminho.
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ ...profileData, cadastro_concluido_em: new Date().toISOString() })
        .eq('id', userId)
        .is('cadastro_concluido_em', null);

      if (profileError) {
        console.error('Error inserting profile:', profileError.message);
      }
    } else {
      // Email confirmation is required — show notice but still allow proceeding to plan selection
      setEmailConfirmRequired(true);
    }

    // 3. Proceed to plan selection
    setLoading(false);
    setStep(2);
  };

  const handleResendConfirmation = async () => {
    setError('');
    setLoading(true);
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: form.email.trim().toLowerCase(),
    });
    if (resendError) {
      setError(resendError.message);
    } else {
      setError('');
      alert('E-mail de confirmação reenviado! Verifique sua caixa de entrada.');
    }
    setLoading(false);
  };

  const handleBackToStep1 = () => {
    setStep(1);
    clearPendingState();
  };

  // Extraido para o formato que `PlanoEscolha` espera (P-41): devolve a
  // mensagem de erro, ou nada quando o redirecionamento para o Stripe deu
  // certo. A mesma funcao, com `cancelUrl` diferente, e usada por quem ja tem
  // conta e chega em `/gestao-plano/plano` sem plano.
  const handleCheckout = async (planoId: string): Promise<string | void> => {
    const basePath = (window as any).__BASE_PATH__ || '';
    const origin = window.location.origin;
    // Direto ao dashboard: a tela intermediaria de sucesso saiu. Ela repetia o
    // que a pessoa acabou de comprar e enfileirava um checklist antes de
    // deixar entrar no produto.
    const successUrl = `${origin}${basePath}/dashboard`;
    const cancelUrl = `${origin}${basePath}/register`;

    try {
      // Quem paga e identificado pelo JWT da sessao, no servidor. A tela nao
      // envia mais o id: um id no corpo da requisicao nao prova identidade
      // nenhuma. Sem sessao ativa nao ha o que provar — o cadastro que ainda
      // depende de confirmacao de e-mail precisa ser confirmado antes de
      // contratar o plano.
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData.session) {
        return 'Confirme o seu e-mail e entre na sua conta para concluir a contratação do plano.';
      }

      const { data, error: fnError } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          planId: planoId,
          successUrl,
          cancelUrl,
        },
      });

      if (fnError || data?.error) {
        // A conta ja assinante e recusada pelo servidor, que e onde a regra
        // vive. Aqui so traduzimos o codigo para uma saida util.
        const body = fnError ? await edgeFunctionErrorBody(fnError) : null;
        if ((data?.codigo || body?.codigo) === 'assinatura_vigente') {
          return 'Esta conta já possui uma assinatura ativa. Acesse Gestão do Plano para trocar de plano.';
        }
        return (
          data?.error ||
          body?.error ||
          fnError?.message ||
          'Erro ao iniciar pagamento. Tente novamente.'
        );
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      return 'Não foi possível iniciar o checkout. Tente novamente.';
    } catch (err: any) {
      return err?.message || 'Erro ao conectar com o Stripe. Tente novamente.';
    }
  };

  return (
    <div className="min-h-screen flex bg-background-50">
      {/* Left side - Promotional card (only in step 1) */}
      {step === 1 && (
        <div className="hidden lg:flex lg:w-[50%] items-center justify-center py-8 pl-8 pr-0 relative">
          <div className="w-full h-full rounded-3xl rounded-r-none p-10 relative overflow-hidden shadow-sm">
            {/* Fundo decorativo. A imagem anterior era gerada pela API do Readdy
                e ficava atrás de um overlay de 80% — dependência externa por
                quase nada na tela. */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-400 via-primary-600 to-primary-800" />
            <div className="absolute inset-0 bg-primary-500/80"></div>

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border border-background-50/15"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 h-52 rounded-full border border-background-50/10"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-full bg-background-50/10"></div>

            <div className="flex flex-col items-center justify-center h-full gap-10 z-10">
              <div className="relative h-80">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="w-24 h-24 rounded-full bg-primary-500 flex items-center justify-center shadow-lg">
                    <Logo className="w-14 h-14 object-contain" monocromatico />
                  </div>
                </div>

                {featureItems.map((item, i) => {
                  const angle = (i * 60 - 90) * (Math.PI / 180);
                  const radius = 130;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  return (
                    <div
                      key={item.label}
                      className="absolute top-1/2 left-1/2 z-10"
                      style={{
                        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                      }}
                    >
                      <div className="flex items-center gap-2 bg-background-50 rounded-full px-3 py-2 shadow-sm border border-background-200/60 whitespace-nowrap">
                        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                          <i className={`${item.icon} text-background-50 text-sm`}></i>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-foreground-800 leading-tight">
                            {item.label}
                          </span>
                          <span className="text-[10px] text-foreground-500 leading-tight">
                            {item.desc}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center">
                <h2 className="text-xl font-bold text-background-50 mb-2">
                  Central Check: Tudo que sua administradora precisa
                </h2>
                <p className="text-sm text-background-200/90 mb-3">
                  Condomínios, alvarás, manutenções, infraestrutura e saúde operacional integrados
                  em poucos cliques.
                </p>
                <p className="text-xs text-background-200/60">Escolha um plano e comece a usar.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right side - Register form (step 1: 50% width, step 2: full width) */}
      <div
        className={`w-full ${step === 1 ? 'lg:w-[50%]' : ''} flex items-start justify-center px-6 md:px-12 py-8 lg:py-12 overflow-y-auto`}
      >
        <div className={`w-full my-auto ${step === 1 ? 'max-w-lg' : 'max-w-7xl'}`}>
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <Link to="/" className="flex items-center gap-2.5">
              <Logo className="w-10 h-10 object-contain" />
              <span className="font-semibold text-2xl tracking-tight text-foreground-900">
                Central Check
              </span>
            </Link>
          </div>

          <h1 className="text-xl font-bold text-foreground-900 mb-1 text-center">Crie sua conta</h1>
          <p className="text-sm text-foreground-500 mb-6 text-center">
            Preencha os dados abaixo para começar.
          </p>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <button
              onClick={() => setStep(1)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                step === 1
                  ? 'bg-primary-500 text-background-50'
                  : step === 2
                    ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                    : 'bg-background-100 text-foreground-500'
              }`}
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === 1
                    ? 'bg-background-50/20 text-background-50'
                    : step === 2
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-300 text-foreground-600'
                }`}
              >
                {step === 2 ? <i className="ri-check-line"></i> : '1'}
              </span>
              Dados
            </button>
            <div
              className={`w-16 h-0.5 rounded-full transition-colors ${step >= 2 ? 'bg-primary-500' : 'bg-background-200'}`}
            ></div>
            <button
              onClick={() => {
                if (step === 2) setStep(2);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                step === 2
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-500'
              }`}
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === 2
                    ? 'bg-background-50/20 text-background-50'
                    : 'bg-background-300 text-foreground-600'
                }`}
              >
                2
              </span>
              Plano
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5 max-w-lg mx-auto">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Registration form */}
          {step === 1 && (
            <div className="max-w-lg mx-auto">
              {/* Account type toggle */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground-800 mb-2">
                  Tipo de conta
                </label>
                <div className="flex gap-2 p-1 rounded-xl bg-background-100">
                  <button
                    type="button"
                    onClick={() => handleTipoChange('pf')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      form.tipo === 'pf'
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i
                      className={`${form.tipo === 'pf' ? 'ri-user-line' : 'ri-user-3-line'} text-base`}
                    ></i>
                    Pessoa Física
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTipoChange('pj')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      form.tipo === 'pj'
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i
                      className={`${form.tipo === 'pj' ? 'ri-building-2-line' : 'ri-building-line'} text-base`}
                    ></i>
                    Pessoa Jurídica
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {form.tipo === 'pf' ? (
                  <>
                    <div>
                      <label
                        htmlFor="nome"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        Nome completo
                      </label>
                      <input
                        id="nome"
                        name="nome"
                        type="text"
                        autoComplete="name"
                        value={form.nome}
                        onChange={(e) => updateField('nome', e.target.value)}
                        placeholder="Seu nome completo"
                        className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="cpf"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        CPF
                      </label>
                      <input
                        id="cpf"
                        name="cpf"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={form.cpf}
                        onChange={(e) => updateField('cpf', mascaraCpf(e.target.value))}
                        placeholder="000.000.000-00"
                        className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label
                        htmlFor="razaoSocial"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        Razão Social
                      </label>
                      <input
                        id="razaoSocial"
                        name="razaoSocial"
                        type="text"
                        autoComplete="organization"
                        value={form.razaoSocial}
                        onChange={(e) => updateField('razaoSocial', e.target.value)}
                        placeholder="Nome da empresa"
                        className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="cnpj"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        CNPJ
                      </label>
                      <input
                        id="cnpj"
                        name="cnpj"
                        type="text"
                        autoCapitalize="characters"
                        autoComplete="off"
                        value={form.cnpj}
                        onChange={(e) => updateField('cnpj', mascaraCnpj(e.target.value))}
                        placeholder="00.000.000/0000-00"
                        className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="nomeResponsavel"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        Nome do responsável
                      </label>
                      <input
                        id="nomeResponsavel"
                        name="nomeResponsavel"
                        type="text"
                        autoComplete="name"
                        value={form.nomeResponsavel}
                        onChange={(e) => updateField('nomeResponsavel', e.target.value)}
                        placeholder="Nome do responsável pela conta"
                        className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    E-mail
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => {
                      updateField('email', e.target.value);
                      // Some enquanto digita: a sugestão é sobre o que já foi
                      // escrito, e mantê-la atrapalha quem está corrigindo.
                      setSugestaoEmail(null);
                    }}
                    onBlur={(e) => setSugestaoEmail(sugerirCorrecaoDeEmail(e.target.value))}
                    placeholder="voce@empresa.com"
                    className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  />

                  {/* Sugere, nunca corrige sozinho: o domínio digitado pode ser
                      real, e trocar por trás da pessoa é pior que o erro. */}
                  {sugestaoEmail && (
                    <p className="mt-1.5 text-sm text-foreground-600">
                      Você quis dizer{' '}
                      <button
                        type="button"
                        onClick={() => {
                          updateField('email', sugestaoEmail);
                          setSugestaoEmail(null);
                        }}
                        className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700"
                      >
                        {sugestaoEmail}
                      </button>
                      ?
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    Telefone / WhatsApp
                  </label>
                  <input
                    id="telefone"
                    name="telefone"
                    type="tel"
                    autoComplete="tel"
                    value={form.telefone}
                    onChange={(e) => updateField('telefone', mascaraTelefone(e.target.value))}
                    placeholder="(11) 99999-9999"
                    className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  />
                </div>

                <div>
                  <label
                    htmlFor="senha"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    Senha
                  </label>
                  <div className="relative">
                    <input
                      id="senha"
                      name="senha"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.senha}
                      onChange={(e) => updateField('senha', e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="w-full px-4 py-3 pr-11 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors"
                      tabIndex={-1}
                    >
                      <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line text-lg`}></i>
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirmarSenha"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    Confirmar senha
                  </label>
                  <div className="relative">
                    <input
                      id="confirmarSenha"
                      name="confirmarSenha"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.confirmarSenha}
                      onChange={(e) => updateField('confirmarSenha', e.target.value)}
                      placeholder="Repita a senha"
                      className="w-full px-4 py-3 pr-11 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors"
                      tabIndex={-1}
                    >
                      <i
                        className={`ri-${showConfirmPassword ? 'eye-off' : 'eye'}-line text-lg`}
                      ></i>
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleNextStep}
                disabled={loading}
                className="w-full mt-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Criando conta...
                  </>
                ) : (
                  <>
                    Continuar para escolher o plano
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Plan selection + checkout - FULL WIDTH LAYOUT */}
          {step === 2 && (
            <div className="w-full">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={handleBackToStep1}
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Voltar e editar dados
                </button>
              </div>

              <h2 className="text-2xl font-bold text-foreground-900 mb-2 text-center">
                Escolha seu plano
              </h2>
              <p className="text-sm text-foreground-500 mb-8 text-center">
                Selecione o plano ideal para sua gestão de condomínios
              </p>

              {/* Email confirmation notice */}
              {emailConfirmRequired && (
                <div className="max-w-2xl mx-auto mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-mail-send-line text-amber-600"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800 mb-1">
                        Verifique seu e-mail para ativar a conta
                      </p>
                      <p className="text-sm text-amber-700 mb-3">
                        Enviamos um link de confirmação para <strong>{form.email}</strong>. Confirme
                        o e-mail e entre na sua conta para escolher o plano e concluir a
                        contratação.
                      </p>
                      <button
                        type="button"
                        onClick={handleResendConfirmation}
                        disabled={loading}
                        className="text-sm font-medium text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Reenviando...' : 'Reenviar e-mail de confirmação'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <PlanoEscolha
                planos={planos}
                titular={form.tipo === 'pf' ? form.nome : form.razaoSocial}
                onCheckout={handleCheckout}
              />
            </div>
          )}

          <p className="mt-8 text-center text-sm text-foreground-600">
            Já tem uma conta?{' '}
            <Link
              to="/login"
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Faça login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
